import Link from "next/link";
import { type FC } from "react";

import type { DocumentPageMetadata } from "@moment/api-collab/api-types";
import { useAuthorsFromGithub, usePageFromCurrentRoute } from "@moment/state";
import {
	Avatar,
	AvatarFallback,
	AvatarImage,
	HoverCard,
	HoverCardContent,
	HoverCardTrigger,
} from "@moment/ui";

import { Row } from "../../layout/row/Row";
import { PagePublishedOnDate } from "./PagePublishedOnDate";

export const Byline: FC<{
	activePageMetadata: DocumentPageMetadata | null;
}> = ({ activePageMetadata }) => {
	const { pageMetadata: routePageMetadata } = usePageFromCurrentRoute();

	// Prefer the page passed in; fall back to route metadata (usePageFromCurrentRoute may be undefined in published SSR)
	const page = activePageMetadata ?? routePageMetadata ?? null;
	const authorId = page?.authorID;
	const { author } = useAuthorsFromGithub(authorId ?? "");

	if (!authorId || !author) {
		return <div className="rich-text my-4" />;
	}

	const profileImageUrl = author?.profileImageUrl;
	const profileImageFallback = author?.profileImageFallback ?? "?";
	const displayName = author?.displayName ?? "--";
	const githubUsername = author?.githubUsername;
	const bio = author?.bio;

	return (
		<div className="rich-text mt-4 mb-8">
			<Row>
				<div className="flex items-center gap-2 justify-center">
					<div className="text-secondary text-base">
						{author ? (
							<HoverCard openDelay={100}>
								<HoverCardTrigger asChild>
									<span className="cursor-pointer text-secondary font-medium underline">
										{displayName}
									</span>
								</HoverCardTrigger>
								<HoverCardContent className="max-w-xs">
									<div className="flex justify-between space-x-4 grow">
										<Avatar size="lg">
											<AvatarImage src={profileImageUrl} />
											<AvatarFallback>{profileImageFallback}</AvatarFallback>
										</Avatar>

										<div className="grow space-y-1 text-sm">
											<div className="text-base font-semibold">
												{displayName}{" "}
												{githubUsername && (
													<Link
														className="text-sm font-light text-secondary hover:text-secondary"
														target="_blank"
														rel="noopener noreferrer"
														href={`https://github.com/${githubUsername}`}
													>
														@{githubUsername}
													</Link>
												)}
											</div>

											{bio && <div className="text-secondary">{bio}</div>}
										</div>
									</div>
								</HoverCardContent>
							</HoverCard>
						) : (
							<span className="text-primary">{displayName}</span>
						)}
					</div>
					{page?.publishedOn && (
						<>
							<span className="text-secondary"> / </span>
							<PagePublishedOnDate pageMetadata={page} />
						</>
					)}
				</div>
			</Row>
		</div>
	);
};
